/**
 * Format API timestamps for display in IST using 12-hour clock (matches typical Indian app UX).
 * Accepts ISO strings (e.g. from toISTISOString), Date instances, or other values Date can parse.
 */
/**
 * List rows like Android ReviewsAdapter: "20 Mar 2026, 10:25 PM" in IST.
 */
export function formatReviewDateTimeIso(input) {
  if (input == null || input === '') return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    return String(input);
  }
  const s = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(d);
  return s.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

/**
 * Calendar date in Asia/Kolkata as YYYY-MM-DD (e.g. list rows).
 */
export function formatDateYYYYMMDDIST(input) {
  if (input == null || input === '') return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    return String(input);
  }
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
}

/**
 * Parse payout history timestamps: ISO or "dd/mm/yyyy, HH:mm:ss" from formatToIST.
 */
export function parsePayoutApiDate(input) {
  if (input == null || input === '') return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})$/.exec(s);
  if (m) {
    const dd = m[1].padStart(2, '0');
    const mm = m[2].padStart(2, '0');
    const yyyy = m[3];
    const hh = m[4].padStart(2, '0');
    const mi = m[5];
    const ss = m[6];
    const t = new Date(`${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}+05:30`);
    return Number.isNaN(t.getTime()) ? null : t;
  }
  const t = new Date(s);
  return Number.isNaN(t.getTime()) ? null : t;
}

/** Payout detail sheet timeline — "14 May 2025, 06:30 pm" in IST. */
export function formatPayoutTimelineDateTime(input) {
  const d = parsePayoutApiDate(input);
  if (!d) return input != null && input !== '' ? String(input) : 'N/A';
  const datePart = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(d);
  const timePart = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(d);
  return `${datePart}, ${timePart.toLowerCase()}`;
}

/** Payout list card footer time — "Mar 22, 3:33 AM" style in IST. */
export function formatPayoutListDateTime(input) {
  const d = parsePayoutApiDate(input);
  if (!d) return input != null && input !== '' ? String(input) : '—';
  const s = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(d);
  return s.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

/** Notification card footer — "Mar 22, 2026 • 03:33 AM" in IST. */
export function formatNotificationCardTimestamp(input) {
  const d = parsePayoutApiDate(input);
  if (!d) return input != null && input !== '' ? String(input) : '—';
  const datePart = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
  const timePart = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(d);
  return `${datePart} • ${timePart.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase())}`;
}

export function formatDateTime12hIST(input) {
  if (input == null || input === '') return '—';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    return String(input);
  }
  const s = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(d);
  return s.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

/**
 * Dashboard analytics chart x-axis: hourly buckets use labels like "14:00" from the API.
 * Show only the clock hour in 12h form (no :00 / :30) — each bucket is a full hour.
 * Week/month labels are unchanged (no HH:mm pattern).
 */
export function formatChartAxisLabel(point) {
  if (!point || point.label == null) return '';
  const label = String(point.label).trim();
  const hm = /^(\d{1,2}):(\d{2})$/.exec(label);
  if (!hm) return label;

  const ts = point.timestamp;
  if (ts) {
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime())) {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: 'numeric',
        hour12: true
      }).format(d);
    }
  }

  const h24 = parseInt(hm[1], 10);
  const min = parseInt(hm[2], 10);
  if (h24 < 0 || h24 > 23 || min < 0 || min > 59) return label;
  const h12 = h24 % 12 || 12;
  const suf = h24 >= 12 ? 'pm' : 'am';
  return `${h12} ${suf}`;
}
